import { DeviceType, HALInterface, BROARDCAST_MAC, HALFrameType, HALEthFrame, IHALInterface, IHALDevice, ISimulatorDevice } from "./Base";
import { sleep, generateRandomMac } from '../../../tools';
import { Node } from "@antv/x6";
const { Netmask } = require('netmask');

// IPV4地址正则
const IP_V4_REGEX = /^[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}$/;

// 广播IP地址
export const BROARDCAST_IP = '255.255.255.255';
export const BLANK_IP = '0.0.0.0';

// 交换机以太网界面，可以接收或者发送以太网帧
export class SwitchInterface extends HALInterface {
    // 其他以太网界面向本界面发送以太网数据
    HAL_Recv(frame: HALEthFrame, callback: Function): void {
        // 构造以太网帧
        const {
            fromMAC,
            toMAC,
            type,
            data,
        } = frame;

        // 自身发送报文丢弃
        if (fromMAC === this.MACAddress) {
            return;
        }

        this.device.HAL_Recv(this, fromMAC, toMAC, type, data, callback);
    }
}

// 集线器（无配置）
export class SwitchDevice implements ISimulatorDevice, IHALDevice {
    protected bridge: any;
    protected node: Node;
    private ports: Array<HALInterface>

    constructor(node: Node, portCount: number) {
        this.node = node;
        this.ports = [];

        // 生成四个端口
        for (let i = 0; i < portCount; i++) {
            this.ports.push(new SwitchInterface(this, generateRandomMac()));
        }
    }

    getNode(): Node {
        return this.node;
    }

    // 配置桥接器，用于变更通知
    setBridge(bridge: any): void {
        this.bridge = bridge;
    }

    get deviceType() {
        return DeviceType.Switch;
    }

    // 断开连线
    destory(): void {
        const { ports } = this;
        for (const port of ports) {
            port.unlink();
        }
    }

    getPorts(): Array<IHALInterface> {
        return this.ports;
    }

    // 转换为JSON
    toJSON() {
        return {
            ports: this.ports.map(port => port.toJSON()),
            deviceType: DeviceType.Switch,
        };
    }

    // 自JSON加载
    fromJSON(config: any): void {
        const { ports } = this;
        for (let i = 0; i < ports.length; i++) {
            const port = ports[i];
            port.fromJSON(config.ports[i]);
        }
    }

    /**
     * 端口接收到请求
     * @param fromMAC 
     * @param toMAC 
     * @param type 
     * @param data 
     * @param callback 
     */
    HAL_Recv(fromPort: IHALInterface, fromMAC: string, toMAC: string, type: HALFrameType, data: any, callback: Function): void {
        const { ports } = this;
        for (const port of ports) {
            // 没有连接电路端口，跳过
            if (!port.connected) {
                continue;
            }

            // 判断是否为发出的电缆，跳过
            if (port === fromPort) {
                continue;
            }

            // 转发消息
            port.HAL_Send_Transparent(fromMAC, toMAC, type, data, callback);
        }
    }
}

// 网络接入层工作模式
enum NACWorkMode {
    Ethernet = 'Ethernet',
    PPPOE = 'PPPOE'
}

export enum ProtocolType {
    DHCP,
    ICMP,
    DNS,
    HTTP,
    HTTPS,
    TCP,
    UDP
};

// IP包（这里偷懒，就不模拟TCP/UDP了）
interface IPPacket {
    fromIP: string;
    toIP: string;
    protocol: ProtocolType,
    data: object,
    TTL: number
}

interface PPPOEPacket {
    type: PPPOE_COMMAND_TYPE
    packet?: IPPacket
    username?: string
    password?: string
}

// IP栈工作模式
export enum IPMode {
    PPPOE = 'PPPOE',
    DHCP = 'DHCP',
    STATIC = 'STATIC'
}

export interface IPConfig {
    ipMode: IPMode // IP模式
    ipAddress: string // IP地址
    subnetMask: string // 子网掩码
    gateway: string // 网关
    dns: string // 域名服务器
    pppoe_username?: string, // PPPOE账号
    pppoe_password?: string, // PPPOE密码
}

interface PPPOEConnectionResponse {
    err?: string,
    mac: string,
    ipConfig?: IPConfig;
}

// PPPOE指令类型
enum PPPOE_COMMAND_TYPE {
    PADI, // 连接
    PPP, // 通信
    PADT, // 断开
};

// DNS报文
interface DNSPacket {
    domainName: string
}

// 网络接入层栈，处理IP协议的ARP部分和PPPOE
class NACStack implements IHALDevice {
    private device: IPStack; // 归属设备
    private PPPOEServerMac: string | null;
    private workMode: NACWorkMode;
    private port: IHALInterface;

    // 归入本栈的端口，ARP表
    constructor(device: IPStack) {
        this.device = device;

        // 根据不同的类型创建端口
        this.port = new HALInterface(this, generateRandomMac());

        this.workMode = NACWorkMode.Ethernet; // 默认为以太网，可以通过拨号转变为PPPOE模式
        this.PPPOEServerMac = null; // 用于接收PPPOE服务器MAC地址
    }

    getPort(): IHALInterface {
        return this.port;
    }

    setPort(port: IHALInterface): void {
        this.port = port;
    }

    // 使用目标查询ARP表获得对应MAC地址
    async ARP_lookup(targetIP: string): Promise<string> {
        // const { device } = this;

        // 查找ARP表
        // let mac = device.searchARP(targetIP);
        // if(mac) {
        //   return Promise.resolve(mac);
        // }

        return new Promise((resolve, reject) => {
            // ARP超时
            let timerID: number = window.setTimeout(() => {
                reject(new Error(`ARP请求目标IP${targetIP}MAC地址超时!`));
            }, 500);

            // 广播ARP包，获取对应主机MAC地址
            this.port.HAL_Send(BROARDCAST_MAC, HALFrameType.ARP, {
                type: 'LOOKUP',
                targetIP,
            }, (responseMac: string) => {
                if (timerID !== 0) {
                    clearTimeout(timerID);
                    timerID = 0;
                }

                // 存入ARP表
                // device.updateARP(targetIP, responseMac);

                // 返回解析结果
                resolve(responseMac);
            });
        })
    }

    // 发送IP报文，不考虑路由表
    async IP_send(fromIP: string, toIP: string, gateway: string, isBroadcast: boolean, protocol: ProtocolType, data: Object, TTL = 64, callback: Function) {
        // 本地没有IP地址，报错退出
        const { port, workMode, PPPOEServerMac } = this;
        if (!port.connected) {
            return;
            // throw new Error('网络接入层尚未建立连接');
        }

        // 打包
        const packet: IPPacket = {
            fromIP,
            toIP,
            protocol,
            data,
            TTL,
        };

        // 工作模式为PPPOE模式，则发送PPP帧
        if (workMode === NACWorkMode.PPPOE) {
            if (!PPPOEServerMac) {
                throw new Error('无法找到PPPOE服务器地址，尚未建立PPPOE连接');
            }

            port.HAL_Send(PPPOEServerMac, HALFrameType.PPPoE, {
                type: PPPOE_COMMAND_TYPE.PPP,
                packet,
            }, callback);

            return;
        }

        // 广播包直接发送，非广播包进行ARP寻址
        let targetMAC: string = BROARDCAST_MAC;
        if (!isBroadcast) {
            // 通过ARP协议查询IP地址
            try {
                // 如果是同一个网络，下一跳就是对端，否则就是网关
                let nextHopIP = (fromIP === gateway) ? toIP : gateway;
                targetMAC = await this.ARP_lookup(nextHopIP);
            }
            catch (error) {
                return Promise.reject(new Error(`IP地址${toIP}对应网络设备MAC地址找不到，该设备可能未上线或IP配置不正确！`));
            }
        }

        // 通过网络访问层发送
        port.HAL_Send(targetMAC, HALFrameType.IPv4, packet, callback);
    }

    /**
     * 端口接收到请求
     * @param fromMAC 
     * @param toMAC 
     * @param type 
     * @param data 
     * @param callback 
     */
    HAL_Recv(fromPort: IHALInterface, fromMAC: string, toMAC: string, type: HALFrameType, data: any, callback: Function): void {
        // 二层基础协议实现
        switch (type) {
            // 处理ARP查询请求
            case HALFrameType.ARP:
                {
                    const { type, targetIP } = data;
                    const { port, device } = this;

                    // 非查询请求不响应
                    if (type !== 'LOOKUP') {
                        return;
                    }

                    const ipconfig = device.getPortIPConfig();

                    // 非自己IP，不响应
                    if (!ipconfig || targetIP !== ipconfig.ipAddress) {
                        return;
                    }

                    // 获取PC机端口MAC地址作为发送MAC地址
                    const fromMAC = port.mac;
                    callback(fromMAC);
                }

                break;

            // 处理PPPOE请求
            case HALFrameType.PPPoE:
                {
                    const { device } = this;

                    if (data.type === PPPOE_COMMAND_TYPE.PADT) {
                        this.PPPOEServerMac = null;
                        this.workMode = NACWorkMode.Ethernet;
                        // 更新设备IP信息
                        device.updateIPConfig(IPStack.getDefaultIPConfig());
                        return;
                    }

                    // 如果设备不能处理PPPOE请求，且为PPP包，则解析为IP包进行处理
                    if (!device.PPPOE_Recv(fromMAC, this.port.mac, data, callback) && data.type === PPPOE_COMMAND_TYPE.PPP) {
                        const { packet } = data;

                        // 解包
                        const {
                            fromIP,
                            toIP,
                            protocol,
                            data: IPData,
                            TTL,
                        } = packet;

                        device.IP_Recv(fromIP, toIP, protocol, IPData, TTL, callback);
                    }
                }
                break;

            // 转发IP请求
            case HALFrameType.IPv4:
                {
                    const { device } = this;

                    // 解包
                    const {
                        fromIP,
                        toIP,
                        protocol,
                        data: IPData,
                        TTL,
                    } = data;

                    device.IP_Recv(fromIP, toIP, protocol, IPData, TTL, callback);
                }
                break;
        }
    }

    // PPPOE联网，联网之后可以通过该栈发送IP包
    async PPPOE_connect(username?: string, password?: string): Promise<IPConfig> {
        const { port, device } = this;

        return new Promise((resolve, reject) => {
            // ARP超时
            let timerID: number = window.setTimeout(() => {
                reject(new Error(`PPPOE拨号连接超时`));
            }, 1000);

            try {
                port.HAL_Send(BROARDCAST_MAC, HALFrameType.PPPoE, {
                    type: PPPOE_COMMAND_TYPE.PADI,
                    username,
                    password,
                    mac: port.mac,
                }, (response: PPPOEConnectionResponse) => {
                    // 清理定时器
                    clearTimeout(timerID);

                    const { err = null, mac, ipConfig = null } = response;
                    if (err !== null) {
                        reject(new Error(err));
                        return;
                    }

                    if (ipConfig === null) {
                        reject(new Error('未获取到IP配置'));
                        return;
                    }

                    // 更新服务器MAC地址
                    this.PPPOEServerMac = mac;

                    // 更新为以太网模式
                    this.workMode = NACWorkMode.PPPOE;

                    // 更新设备IP信息
                    device.updateIPConfig(ipConfig);

                    resolve(ipConfig);
                });
            }
            catch (e) {
                reject(e);
            }
        });
    }

    // PPPOE离线，主动发送PADT包，释放IP
    PPPOE_disconnect(): void {
        if (!this.PPPOEServerMac) {
            return;
        }

        const { port } = this;

        port.HAL_Send(this.PPPOEServerMac, HALFrameType.PPPoE, {
            type: PPPOE_COMMAND_TYPE.PADT,
            mac: port.mac,
        }, () => {
            this.PPPOEServerMac = null;
            this.workMode = NACWorkMode.Ethernet;
        });
    }

    toJSON(): any {
        const { PPPOEServerMac, workMode, port } = this;

        return {
            PPPOEServerMac,
            workMode,
            port: port.toJSON()
        };
    }

    fromJSON(config: any): void {
        this.PPPOEServerMac = config.PPPOEServerMac;
        this.workMode = config.workMode;
        this.port.fromJSON(config.port);
    }
}

// 路由表
interface RouterTableItem {
    destination: string
    netmask: string
    gateway: string
    interface: string
    metric: number
    block: any
    mac: string
    isBroadcast: boolean
}

// DHCP指令类型
enum DHCP_COMMAND_TYPE {
    DISCOVER,
    RELEASE,
};

class HTTPError extends Error {
    public status: string;
    public title: string;
    public subTitle?: string;
    constructor(status: string, title: string, subTitle?: string) {
        super(status);
        this.status = status;
        this.title = title;
        this.subTitle = subTitle;
    }
}

interface HTTPResponse {
    code: Number,
    title: string
    subTitle?: string,
}

// IP栈
export class IPStack {
    // IP配置
    protected ipConfig: IPConfig;

    // NAC栈
    protected networkCard: NACStack;

    // ARP表，记录IP与MAC地址关系
    protected arpTable: { [id: string]: string; };

    // 路由表，记录到访哪个IP走哪个端口和网关
    protected routerTable: Array<RouterTableItem> = [];

    // 服务器应用
    protected apps: Array<IPServerApplication> = [];

    constructor(ipConfig = IPStack.getDefaultIPConfig()) {
        this.ipConfig = ipConfig;
        this.networkCard = new NACStack(this);
        this.arpTable = {};
        this.routerTable = [];
    }

    static getDefaultIPConfig() {
        return {
            ipMode: IPMode.STATIC, // IP模式
            // ipAddress: '192.168.31.1', // IP地址
            // subnetMask: '255.255.255.0', // 子网掩码
            ipAddress: BLANK_IP, // IP地址
            subnetMask: BLANK_IP, // 子网掩码
            gateway: BLANK_IP, // 网关
            dns: BLANK_IP, // 域名服务器
        };
    }

    // 安装应用
    install(app: IPServerApplication) {
        this.apps.push(app);
    }

    // 卸载应用
    uninstall(app: IPServerApplication) {
        const index = this.apps.indexOf(app);
        this.apps.splice(index, 1);
    }

    // 持久化路由表
    // 主要处理其中的网络块
    routerTableToJSON() {
        return this.routerTable.map((routerTableItem) => {
            if (!routerTableItem.block) {
                return routerTableItem;
            }
            else {
                const { base, mask } = routerTableItem.block;
                return {
                    ...routerTableItem,
                    block: {
                        base,
                        mask
                    }
                }
            }
        });
    }

    // 自JSON恢复
    routerTableFromJSON(routerTable: any) {
        return routerTable.map((routerTableItem: RouterTableItem) => {
            if (!routerTableItem.block) {
                return routerTableItem;
            }
            else {
                const { base, mask } = routerTableItem.block;
                return {
                    ...routerTableItem,
                    block: new Netmask(`${base}/${mask}`)
                }
            }
        });
    }

    toJSON() {
        const { networkCard, ipConfig, arpTable } = this;
        return {
            networkCard: networkCard.toJSON(),
            ipConfig,
            arpTable,
            routerTable: this.routerTableToJSON(),
        };
    }

    fromJSON(config: any): void {
        const { networkCard } = this;
        networkCard.fromJSON(config.networkCard);
        this.arpTable = config.arpTable;
        this.ipConfig = config.ipConfig;
        this.routerTable = this.routerTableFromJSON(config.routerTable);
    }

    async destory() {
        const { ipConfig } = this;
        switch (ipConfig.ipMode) {
            case IPMode.DHCP:
                // DHCP模式，尝试释放IP地址
                try {
                    await this.DHCP_release();
                }
                catch(e) {
                    console.error(e);
                }
                break;
            case IPMode.PPPOE:
                try {
                    // PPPOE模式，尝试释放PPPOE IP地址
                    await this.PPPOE_disconnect();
                }
                catch(e) {
                    console.error(e);
                }
                break;
            case IPMode.STATIC:
                break;
            default:
                throw new Error(`不受支持的工作模式${ipConfig.ipMode}`);
        }

        // 断开网卡连线
        const { networkCard } = this;
        networkCard.getPort().unlink();
    }

    getPorts(): IHALInterface[] {
        const { networkCard } = this;
        return [networkCard.getPort()];
    }

    searchARP(ip: string): string {
        return this.arpTable[ip];
    }

    updateARP(ip: string, mac: string): void {
        this.arpTable[ip] = mac;
    }

    removeARP(ip: string): void {
        delete this.arpTable[ip];
    }

    /**
     * 生成路由表
     * https://blog.csdn.net/zyboy2000/article/details/50528253
     */
    generateRouterTable(): void {
        const { routerTable, ipConfig, networkCard } = this;

        // 获取MAC
        const mac = networkCard.getPort().mac;

        // 清理路由表
        routerTable.length = 0;

        // 根据网卡配置生成路由表条目
        const { ipAddress, subnetMask, gateway } = ipConfig;

        // 有效IP配置为路由表条目
        if ((ipAddress !== BLANK_IP) && (subnetMask !== BLANK_IP)) {
            const block = new Netmask(`${ipAddress}/${subnetMask}`);

            // 生成路由表条目
            const baseItem: RouterTableItem = {
                destination: block.base,
                netmask: subnetMask,
                gateway: ipAddress,
                interface: ipAddress,
                metric: 20,
                block,
                mac,
                isBroadcast: false,
            };

            routerTable.push(baseItem);
        }

        // 加入默认路由
        if (gateway !== BLANK_IP) {
            const item: RouterTableItem = {
                destination: BLANK_IP,
                netmask: BLANK_IP,
                gateway: gateway,
                interface: ipAddress,
                metric: 30,
                block: null,
                mac,
                isBroadcast: false,
            };

            routerTable.unshift(item);
        }

        // 加入广播路由
        const broadcastItem: RouterTableItem = {
            destination: BROARDCAST_IP,
            netmask: BROARDCAST_IP,
            gateway: ipAddress,
            interface: ipAddress,
            metric: 1,
            block: null,
            mac,
            isBroadcast: true,
        };

        routerTable.push(broadcastItem);
    }

    /**
     * 查询路由表目标条目，确定发送出去的端口信息
     * @param targetIP 
     */
    getRouterByTargetIP(targetIP: string): RouterTableItem {
        const { routerTable } = this;

        // 如果路由表条目为空，生成默认路由表
        if (!routerTable.length) {
            this.generateRouterTable();
        }

        const targetItems: Array<RouterTableItem> = [];
        const defaultItems: Array<RouterTableItem> = [];

        // 循环路由表条目，计算这条记录落在哪几个路由表条目中，比选处跃点数最低的那个作为查找结果
        for (const routerTableItem of routerTable) {
            // 合并默认路由
            if (routerTableItem.destination === BLANK_IP) {
                defaultItems.push(routerTableItem);
                continue;
            }

            // 完全匹配的情况也作为目标路由
            if (routerTableItem.destination === targetIP) {
                targetItems.push(routerTableItem);
                continue;
            }

            // 包含目标IP条目合并
            if (routerTableItem.block && routerTableItem.block.contains(targetIP)) {
                targetItems.push(routerTableItem);
                continue;
            }
        }

        // 优先使用目标路由
        if (targetItems.length) {
            // 按照metric排序
            targetItems.sort((a, b) => a.metric - b.metric);
            return targetItems[0];
        }

        // 按照metric排序
        defaultItems.sort((a, b) => a.metric - b.metric);
        return defaultItems[0];
    }

    // 查找路由表的接收条目
    getRouterByFromIP(fromIP: String): RouterTableItem | null {
        const { routerTable } = this;

        // 如果路由表条目为空，生成默认路由表
        if (!routerTable.length) {
            this.generateRouterTable();
        }

        // 如果来源为广播地址，那么看看是哪一块网卡的IP
        if (fromIP === BROARDCAST_IP) {
            const routerTableItem = routerTable.find(item => item.isBroadcast);
            return routerTableItem ? routerTableItem : null;
        }

        // 循环路由表条目，计算这条记录落在哪几个路由表条目中，比选处跃点数最低的那个作为查找结果
        for (const routerTableItem of routerTable) {
            // 如果来源IP命中接口
            if (fromIP === routerTableItem.interface) {
                return routerTableItem;
            }

            // 如果来源IP命中广播地址
            if (routerTableItem.block) {
                if (fromIP === routerTableItem.block.broadcast) {
                    return routerTableItem;
                }
            }
        }

        return null;
    }

    /**
     * IP层发送
     * @param data 数据内容
     * @param protocol 协议名称
     * @param toIP 目标IP
     * @param callback 回调函数
     * @param TTL 报文生存期 每经过一个设备都会减一
     */
    async IP_send(toIP: string, protocol: ProtocolType, data: Object, TTL = 64, callback: Function) {
        const { networkCard } = this;

        // 如果是本机环回
        if (toIP === this.ipConfig.ipAddress || toIP === BROARDCAST_IP) {
            this.IP_Recv(toIP, toIP, protocol, data, TTL, callback);

            if (toIP !== BROARDCAST_IP) {
                return;
            }
        }

        // 根据toIP查询路由表
        const routerTableItem = this.getRouterByTargetIP(toIP);
        if (!routerTableItem) {
            return;
        }

        const { interface: fromIP, gateway, isBroadcast } = routerTableItem;

        await networkCard.IP_send(fromIP, toIP, gateway, isBroadcast, protocol, data, TTL, callback);
    }

    // PPPOE拨号成功或者DHCP客户端获得IP后更新配置信息
    updateIPConfig(ipconfig: IPConfig): void {
        this.ipConfig = ipconfig;

        // 清理路由表和ARP表配置
        this.arpTable = {};
        this.generateRouterTable();
    }

    getPortIPConfig(): IPConfig {
        return this.ipConfig;
    }

    /**
     * DNS查询
     * @param targetDomain 目标域名，也可以是IP
     * @returns 
     */
    async DNS_Lookup(targetDomain: string): Promise<string> {
        const { dns } = this.ipConfig;

        // 本身就是IP地址，就直接返回了
        if (targetDomain.match(IP_V4_REGEX)) {
            return targetDomain;
        }

        if (dns === BLANK_IP) {
            throw new Error('没有配置DNS服务器');
        }

        return new Promise((resolve, reject) => {
            // DNS超时
            let timerID: number = 0;
            let finished = 0;

            timerID = window.setTimeout(() => {
                reject(new Error(`DNS解析失败，没有在5秒内收到服务器的响应`));
            }, 5000);

            // 以广播方式发出DHCP Discover报文
            this.IP_send(dns, ProtocolType.DNS, { domainName: targetDomain }, 16, (response: any) => {
                if (!response) {
                    reject(new Error(`DNS解析失败，没有对应${targetDomain}的记录`));
                    return;
                }

                // 多DHCP仅处理第一条
                if (finished !== 0) {
                    return;
                }

                // 清理超时
                clearTimeout(timerID);
                timerID = 0;

                // 多DHCP仅处理第一条
                finished = 1;

                // 返回IP
                resolve(response.ip);
            }).catch((e) => reject(e));
        });
    }

    /**
     * ICMP Ping实现
     * @param target 目标IP / 域名
     * @param callback 回调
     * @param times 尝试次数
     * @param duration 间隔周期
     * @param TTL 报文生命周期
     */
    async ICMP_ping(target: string, callback: Function, times = 4, duration = 500, TTL: number = 64) {
        // 判断是IP还是域名，如果是域名，进行DNS解析先
        let targetIP = target;
        if (!target.match(IP_V4_REGEX)) {
            try {
                targetIP = await this.DNS_Lookup(target);
            }
            catch (e) {
                callback({ result: 'error', error: e });
                return;
            }
        }

        for (let seq = 0; seq < times; seq++) {
            let timerID: number = 0;
            try {
                // Ping超时处理逻辑
                timerID = window.setTimeout(() => {
                    callback({ result: 'error', error: '主机没有响应' });
                }, 1000);

                // 发送ICMP Ping包，获取对应主机MAC地址
                await this.IP_send(targetIP, ProtocolType.ICMP, seq, TTL, (packet: IPPacket) => {
                    if (timerID !== 0) {
                        clearTimeout(timerID)
                    }

                    timerID = 0
                    callback({ result: 'success', packet });
                })

            }
            catch (error) {
                if (timerID !== 0) {
                    clearTimeout(timerID)
                }
                timerID = 0

                callback({ result: 'error', error });
            }

            await sleep(duration);
        }
    }

    /**
     * HTTP Get实现
     * @param target 目标IP / 域名
     * @param callback 回调
     * @param times 尝试次数
     * @param duration 间隔周期
     * @param TTL 报文生命周期
     */
    async HTTP_get(urlText: string) {
        if (!urlText.match(/^([^:]+):\/\//)) {
            urlText = `http://${urlText}`;
        }

        // 检查URL协议
        const url = new URL(urlText);
        if ((url.protocol !== 'http:') && (url.protocol !== 'https:')) {
            throw new HTTPError('error', 'URL解析错误', `不支持的协议类型${url.protocol}，请检查您输入的URL地址！`);
        }

        // 解析主机域名
        let hostIP: string;
        try {
            hostIP = await this.DNS_Lookup(url.hostname);
        }
        catch (e: any) {
            throw new HTTPError('error', 'DNS域名解析错误', e.message);
        }

        return new Promise((resolve, reject) => {
            let timerID: number = 0;

            // HTTP超时
            timerID = window.setTimeout(() => {
                reject(new HTTPError('error', '无法建立HTTP连接', `请检查服务器${hostIP}是否启用了HTTP服务`));
            }, 2000);

            // 发送HTTP包
            this.IP_send(hostIP, ProtocolType.HTTP, url, 64, (response: HTTPResponse) => {
                if (timerID !== 0) {
                    clearTimeout(timerID)
                }

                timerID = 0

                if (response.code === 200) {
                    resolve(response);
                }
                else {
                    const { title, subTitle } = response;
                    reject(new HTTPError('error', title, subTitle));
                }
            }).catch((e) => {
                reject(new HTTPError('error', '无法建立HTTP连接', `请检查服务器${hostIP}是否启用了HTTP服务，${e.message}`));
            });

        });
    }

    getIPConfig(): IPConfig {
        return this.ipConfig;
    }

    // 实现ICMP协议的基于IP地址的PING以及响应
    IP_Recv(fromIP: string, toIP: string, protocol: ProtocolType, data: object, TTL: number, callback: Function): any {
        // TTL生存期逻辑，每一跳减一
        TTL--;

        // TTL为零包应当丢弃
        if (TTL <= 0) {
            return null;
        }

        // 检查是否为需要处理的IP包，否则丢弃
        const routerTableItem = this.getRouterByFromIP(toIP);
        if (!routerTableItem) {
            return null;
        }

        switch (protocol) {
            case ProtocolType.ICMP:
                {
                    const { ipConfig } = this;
                    const responsePacket = {
                        data,
                        TTL,
                        fromIP: ipConfig.ipAddress,
                        toIP: fromIP
                    };

                    callback(responsePacket);
                }
                break;
            default:
                {
                    // TODO 这里应该是实现端口号对应进程号逻辑简化了，简化了~
                    // 处理应用层逻辑
                    const { apps } = this;
                    for (const app of apps) {
                        // 报文没有处理掉就每个应用试试看
                        const result = app.IP_Recv(this.getIPConfig(), fromIP, toIP, protocol, data, TTL, callback);
                        if (result) {
                            return;
                        }
                    }
                }
                break;

        }

        return null;
    }

    async PPPOE_connect(): Promise<IPConfig> {
        const { networkCard, ipConfig } = this;
        const { pppoe_username: username, pppoe_password: password } = ipConfig;

        return await networkCard.PPPOE_connect(username, password);
    }

    PPPOE_Recv(fromMac: string, portMac: string, packet: PPPOEPacket, callback: Function): boolean {
        throw new Error('Method not implemented.');
    }

    PPPOE_disconnect() {
        const { networkCard } = this;
        networkCard.PPPOE_disconnect();
    }

    // DHCP获取地址
    async DHCP_discover(): Promise<IPConfig> {
        // 配置DHCP模式
        const { ipConfig, networkCard } = this;
        ipConfig.ipMode = IPMode.DHCP;
        ipConfig.ipAddress = BLANK_IP;
        ipConfig.subnetMask = '255.255.255.0';
        ipConfig.gateway = BLANK_IP;
        ipConfig.dns = BLANK_IP;

        // 清理路由表和ARP表配置
        this.arpTable = {};
        this.routerTable.length = 0;

        // 获取网卡IP
        const mac = networkCard.getPort().mac;

        return new Promise((resolve, reject) => {
            // DHCP超时
            let timerID: number = 0;
            let finished = 0;

            timerID = window.setTimeout(() => {
                reject(new Error(`请求DHCP服务器分配IP地址超时!`));
            }, 5000);

            // 以广播方式发出DHCP Discover报文
            this.IP_send(BROARDCAST_IP, ProtocolType.DHCP, { type: DHCP_COMMAND_TYPE.DISCOVER, mac }, 16, (response: any) => {
                if (!response) {
                    reject(new Error('因地址池已耗尽，无法分配IP地址!'));
                    return;
                }

                // 多DHCP仅处理第一条
                if (finished !== 0) {
                    return;
                }

                // 清理超时
                clearTimeout(timerID);
                timerID = 0;

                // 调试用
                // console.log('DHCP获取IP成功，更新路由表', this.routerTable);

                // 修改本地IP栈信息
                this.updateIPConfig(response.ipConfig);

                // 多DHCP仅处理第一条
                finished = 1;

                resolve(this.ipConfig);
            }).catch((e) => reject(e));
        });
    }

    // DHCP释放地址
    async DHCP_release(): Promise<void> {
        // 获取网卡IP
        const mac = this.networkCard.getPort().mac;

        // 以广播方式发出DHCP Discover报文
        return new Promise((resolve) => {
            this.IP_send(BROARDCAST_IP, ProtocolType.DHCP, { type: DHCP_COMMAND_TYPE.RELEASE, mac }, 16, (response: any) => {
                resolve();
            });
        });
    }
}

// IP应用程序
enum IPServerApplicationType {
    DHCP = 'DHCP',
    NAT = 'NAT',
}

// IP服务器应用
abstract class IPServerApplication {
    get appType(): IPServerApplicationType {
        throw new Error('请继承并实现');
    }

    fromJSON(config: any): void {
        throw new Error('请继承并实现');
    }

    toJSON() {
        return {
            appType: this.appType,
        }
    }

    /**
     * 
     * @param fromIP 
     * @param toIP 
     * @param protocol 
     * @param data 
     * @param TTL 
     * @param callback 
     * @returns true代表报文已经被消费停止其他应用处理，false代表报文尚未被消费继续尝试其他应用处理
     */
    IP_Recv(ipConfig: IPConfig, fromIP: string, toIP: string, protocol: ProtocolType, data: object, TTL: number, callback: Function): Boolean {
        return false;
    }
}

// DHCP服务器应用
export class DHCPServerApplication extends IPServerApplication {
    protected enabled: Boolean = false; // 使能开关
    protected ipPoolRangeStart: number = 100; // 开始IP
    protected ipPoolRangeEnd: number = 199; // 结束IP

    protected dhcpIP2MacMap: { [ip: string]: string; } = {}
    protected dhcpMac2IPMap: { [mac: string]: string; } = {}

    constructor(enabled: Boolean, ipPoolRangeStart = 100, ipPoolRangeEnd = 199) {
        super();
        this.enabled = enabled;
        this.ipPoolRangeStart = ipPoolRangeStart;
        this.ipPoolRangeEnd = ipPoolRangeEnd;
    }

    get appType(): IPServerApplicationType {
        return IPServerApplicationType.DHCP;
    }

    fromJSON(config: any): void {
        this.ipPoolRangeStart = config.ipPoolRangeStart;
        this.ipPoolRangeEnd = config.ipPoolRangeEnd;
        this.dhcpIP2MacMap = config.dhcpIP2MacMap;
        this.dhcpMac2IPMap = config.dhcpMac2IPMap;
        this.enabled = config.enabled;
    }

    toJSON() {
        const { ipPoolRangeStart, ipPoolRangeEnd, dhcpIP2MacMap, dhcpMac2IPMap, enabled } = this;

        return {
            ...super.toJSON(),
            ipPoolRangeStart,
            ipPoolRangeEnd,
            dhcpIP2MacMap,
            dhcpMac2IPMap,
            enabled,
        };
    }

    IP_Recv(ipConfig: IPConfig, fromIP: string, toIP: string, protocol: ProtocolType, data: object, TTL: number, callback: Function): Boolean {
        if (!this.enabled) {
            return false;
        }

        // 如果底层已经处理好了，那就不处理了
        // 只处理DHCP报文
        if (ProtocolType.DHCP !== protocol) {
            console.error('DHCP Server收到了非法报文');
            return false;
        }

        const { type, mac } = data as any;
        switch (type) {
            case DHCP_COMMAND_TYPE.DISCOVER:
                {
                    const { ipPoolRangeStart, ipPoolRangeEnd } = this;
                    const { ipAddress: mainAddress, subnetMask, gateway, dns } = ipConfig;

                    // 检查MAC是否已经被分配IP了
                    let ipAddress = this.dhcpMac2IPMap[mac];

                    // 如果没有分配IP
                    if (!ipAddress) {
                        // DHCP服务器被配置为空IP地址
                        if ((mainAddress === BLANK_IP) || (subnetMask === BLANK_IP)) {
                            return true;
                        }

                        // 通过自身IP与子网掩码生成网段
                        const block = new Netmask(`${mainAddress}/${subnetMask}`);
                        block.forEach((ip: string) => {
                            // 有了就跳过
                            if (ipAddress) {
                                return;
                            }

                            // 如果IP不在允许范围内，返回空跳过
                            const parts = ip.match(/\.(\d+)$/);
                            if (!parts) {
                                return;
                            }

                            const ipIndex = parseInt(parts[1]);
                            if ((ipIndex < ipPoolRangeStart) || (ipIndex > ipPoolRangeEnd)) {
                                return;
                            }

                            // 如果IP在范围内，那么是否已经被分配了，已经被分配了，跳过
                            if (this.dhcpIP2MacMap[ip]) {
                                return;
                            }

                            // 那么分配这个IP
                            ipAddress = ip;
                            this.dhcpIP2MacMap[ip] = mac;
                            this.dhcpMac2IPMap[mac] = ip;
                        })
                    }

                    // IP池用尽
                    if (!ipAddress) {
                        callback(null);
                        return true;
                    }

                    // 返回DHCP Discover包
                    const nextIPConfig: IPConfig = {
                        ipMode: IPMode.DHCP,
                        ipAddress,
                        subnetMask,
                        gateway,
                        dns
                    };

                    callback({ ipConfig: nextIPConfig });
                }
                break;
            case DHCP_COMMAND_TYPE.RELEASE:
                {
                    // 检查MAC是否已经被分配IP了
                    const ipAddress = this.dhcpMac2IPMap[mac];
                    if (ipAddress) {
                        delete this.dhcpIP2MacMap[ipAddress];
                        delete this.dhcpMac2IPMap[mac];
                    }

                    callback();
                }
                break;
        }

        return true;
    }

    updateDHCPPool(enabled: Boolean, ipPoolRangeStart: number, ipPoolRangeEnd: number) {
        this.enabled = enabled;
        this.ipPoolRangeStart = ipPoolRangeStart;
        this.ipPoolRangeEnd = ipPoolRangeEnd;
        this.dhcpIP2MacMap = {};
        this.dhcpMac2IPMap = {};
    }
}

// NAT服务器应用

// 计算机
export class PCDevice extends IPStack implements ISimulatorDevice {
    protected node: Node;
    protected bridge: any;

    constructor(node: Node) {
        super();
        this.node = node;
    }

    getNode(): Node {
        return this.node;
    }

    get deviceType() {
        return DeviceType.PC;
    }

    // 配置桥接器，用于变更通知
    setBridge(bridge: any): void {
        this.bridge = bridge;
    }

    // PPPOE拨号成功或者DHCP客户端获得IP后更新配置信息
    updateIPConfig(ipConfig: IPConfig): void {
        super.updateIPConfig(ipConfig);
        this.bridge.onChange();
    }

    toJSON() {
        return {
            ...super.toJSON(),
            deviceType: DeviceType.PC,
        };
    }

    async DHCP_discover(): Promise<IPConfig> {
        const ipConfig = await super.DHCP_discover();
        this.bridge.onChange();
        return Promise.resolve(ipConfig);
    }

}

// DHCP服务器
export class DHCPServerDevice extends PCDevice {
    protected dhcpServerApp: DHCPServerApplication;

    constructor(node: Node, ipConfig = {
        ipMode: IPMode.STATIC, // IP模式
        // ipAddress: '192.168.31.253', // IP地址
        // subnetMask: '255.255.255.0', // 子网掩码
        ipAddress: BLANK_IP, // IP地址
        subnetMask: BLANK_IP, // 子网掩码
        gateway: BLANK_IP, // 网关
        dns: BLANK_IP, // 域名服务器
    }, ipPoolRangeStart = 100, ipPoolRangeEnd = 199) {
        super(node);
        this.ipConfig = ipConfig;
        this.dhcpServerApp = new DHCPServerApplication(true, ipPoolRangeStart, ipPoolRangeEnd);
        this.install(this.dhcpServerApp);
    }

    get deviceType() {
        return DeviceType.DHCPServer;
    }

    fromJSON(config: any): void {
        super.fromJSON(config);
        this.dhcpServerApp.fromJSON(config);
    }

    toJSON() {
        const dhcpServerConfig = this.dhcpServerApp.toJSON();

        return {
            ...super.toJSON(),
            ...dhcpServerConfig,
            deviceType: DeviceType.DHCPServer,
        };
    }

    updateDHCPPool(ipPoolRangeStart: number, ipPoolRangeEnd: number) {
        this.dhcpServerApp.updateDHCPPool(true, ipPoolRangeStart, ipPoolRangeEnd);
    }
}

// 路由器IP栈
class RouterIPStack extends IPStack {
    protected device: RouterDevice;

    constructor(ipConfig: IPConfig, device: RouterDevice) {
        super(ipConfig);
        this.device = device;
    }

    // 实现ICMP协议的基于IP地址的PING以及响应
    IP_Recv(fromIP: string, toIP: string, protocol: ProtocolType, data: object, TTL: number, callback: Function): any {
        // TTL生存期逻辑，每一跳减一
        TTL--;

        // TTL为零包应当丢弃
        if (TTL <= 0) {
            return null;
        }

        // 检查是否为需要处理的IP包，否则转发给路由器设备处理
        const routerTableItem = this.getRouterByFromIP(toIP);
        if (!routerTableItem) {
            return this.device.RouterIP_Recv(this, fromIP, toIP, protocol, data, TTL, callback);
        }

        switch (protocol) {
            case ProtocolType.ICMP:
                {
                    const { ipConfig } = this;
                    const responsePacket = {
                        data,
                        TTL,
                        fromIP: ipConfig.ipAddress,
                        toIP: fromIP
                    };

                    callback(responsePacket);
                }
                break;
            default:
                {
                    // TODO 这里应该是实现端口号对应进程号逻辑简化了，简化了~
                    // 处理应用层逻辑
                    const { apps } = this;
                    for (const app of apps) {
                        // 报文没有处理掉就每个应用试试看
                        const result = app.IP_Recv(this.getIPConfig(), fromIP, toIP, protocol, data, TTL, callback);
                        if (result) {
                            return;
                        }
                    }
                }
                break;
        }

        return null;
    }

    getIPConfig(): IPConfig {
        return this.device.getIPConfig();
    }
}

// 路由器
export class RouterDevice implements ISimulatorDevice {
    protected node: Node;
    protected bridge: any;
    protected lan: RouterIPStack;
    protected wan: RouterIPStack;

    constructor(node: Node) {
        this.node = node;
        this.lan = new RouterIPStack({
            ipMode: IPMode.STATIC, // IP模式
            ipAddress: '192.168.31.1', // IP地址
            subnetMask: '255.255.255.0', // 子网掩码
            gateway: BLANK_IP, // 网关
            dns: BLANK_IP, // 域名服务器
        }, this);

        this.wan = new RouterIPStack({
            ipMode: IPMode.STATIC, // IP模式
            ipAddress: '0.0.0.0', // IP地址
            subnetMask: '0.0.0.0', // 子网掩码
            gateway: BLANK_IP, // 网关
            dns: BLANK_IP, // 域名服务器
        }, this);

        this.onConfigChange = this.onConfigChange.bind(this);
    }

    getNode(): Node<Node.Properties> {
        return this.node;
    }

    get deviceType() {
        return DeviceType.Router;
    }

    // 启动保存流程用
    onConfigChange() {
        this.bridge.onChange();
    }

    getLan(): RouterIPStack {
        return this.lan;
    }

    getWan(): RouterIPStack {
        return this.wan;
    }

    setBridge(bridge: any): void {
        this.bridge = bridge;
    }

    getPorts(): IHALInterface[] {
        return [...this.lan.getPorts(), ...this.wan.getPorts()];
    }

    destory() {
        this.lan.destory();
        this.wan.destory();
    }

    toJSON() {
        return {
            lan: this.lan.toJSON(),
            wan: this.wan.toJSON(),
            deviceType: DeviceType.Router,
        }
    }

    fromJSON(config: any): void {
        this.lan.fromJSON(config.lan);
        this.wan.fromJSON(config.wan);
    }

    // 获取给应用层用的IP配置，这里主要是给DHCP服务器用的
    getIPConfig(): IPConfig {
        // 从LAN口获得IP、子网掩码
        const lanIPConfig = this.lan.getPortIPConfig();

        // 从WAN口获得网关，DNS
        const { dns } = this.wan.getPortIPConfig();

        const ipconfig: IPConfig = {
            ...lanIPConfig,
            gateway: lanIPConfig.ipAddress,
            dns
        }

        return ipconfig;
    }

    RouterIP_Recv(stack: RouterIPStack, fromIP: string, toIP: string, protocol: ProtocolType, data: object, TTL: number, callback: Function): any {
        // 网关收到报文
        // 从LAN到WAN
        if (this.lan === stack) {
            // 验证接收IP是否在WAN口路由表内，如果在则用自己IP转发
            const routerTableItem = this.wan.getRouterByTargetIP(toIP);
            if (!routerTableItem) {
                return null;
            }

            this.wan.IP_send(toIP, protocol, data, TTL, callback);
        }

        // 从WAN到LAN
        if (this.wan === stack) {
            // 验证接收IP是否在LAN口路由表内，如果在则用自己IP转发
            const routerTableItem = this.lan.getRouterByTargetIP(toIP);
            if (!routerTableItem) {
                return null;
            }

            this.lan.IP_send(toIP, protocol, data, TTL, callback);
        }
    }
}

// 调制解调器
export class ModemDevice extends IPStack implements ISimulatorDevice {
    protected node: Node;
    protected bridge: any;
    protected ipPoolRangeStart: number = 2; // 开始IP
    protected ipPoolRangeEnd: number = 254; // 结束IP

    protected pppoeIP2MacMap: { [ip: string]: string; } = {}
    protected pppoeMac2IPMap: { [mac: string]: string; } = {}

    protected dnsRecords = [
        { domainName: 'www.baidu.com', ip: '103.231.13.42' },
        { domainName: 'www.iyopu.com', ip: '114.215.113.173' },
        { domainName: 'www.nsfz.cn', ip: '218.94.25.46' },
        { domainName: 'baidu.com', ip: '103.231.13.42' },
        { domainName: 'iyopu.com', ip: '114.215.113.173' },
    ];

    protected pppoeAuth = {
        username: '02583466679',
        password: '889977',
    };

    constructor(node: Node) {
        // Modem是模拟了公网网关设备的
        super({
            ipMode: IPMode.STATIC,
            ipAddress: '49.77.221.1',
            subnetMask: '255.255.255.0',
            gateway: '49.77.221.1',
            dns: '218.2.135.1'
        });

        this.node = node;
    }

    getNode(): Node<Node.Properties> {
        return this.node;
    }

    get deviceType() {
        return DeviceType.Modem;
    }

    get dnsTable() {
        return this.dnsRecords;
    }

    static getDefaultPPPOEAuth() {
        return {
            username: '02583466679',
            password: '889977',
        };
    }

    static getDefaultDNSRecords() {
        return [
            { domainName: 'www.baidu.com', ip: '103.231.13.42' },
            { domainName: 'www.iyopu.com', ip: '114.215.113.173' },
            { domainName: 'www.nsfz.cn', ip: '218.94.25.46' },
            { domainName: 'baidu.com', ip: '103.231.13.42' },
            { domainName: 'iyopu.com', ip: '114.215.113.173' },
        ];
    }

    getPPPOEAuth() {
        return { ...this.pppoeAuth };
    }

    updatePPPOEAuth(username: string, password: string) {
        // 入参检查，任何为空则不处理，避免脏数据
        if (!username || !password) {
            return;
        }

        this.pppoeAuth = { username, password };
        // 触发上层同步
        if (this.bridge && this.bridge.onGraphChange) {
            this.bridge.onGraphChange();
        }
    }

    updateDNSRecords(records: Array<{ domainName: string; ip: string }>) {
        if (!records || !Array.isArray(records)) {
            return;
        }

        this.dnsRecords = records
            .filter((item) => item && item.domainName && item.ip)
            .map((item) => ({
                domainName: item.domainName,
                ip: item.ip
            }));

        if (!this.dnsRecords.length) {
            this.dnsRecords = ModemDevice.getDefaultDNSRecords();
        }

        if (this.bridge && this.bridge.onGraphChange) {
            this.bridge.onGraphChange();
        }
    }

    resetToDefaultConfigs() {
        this.pppoeAuth = ModemDevice.getDefaultPPPOEAuth();
        this.dnsRecords = ModemDevice.getDefaultDNSRecords();

        if (this.bridge && this.bridge.onGraphChange) {
            this.bridge.onGraphChange();
        }
    }

    // 配置桥接器，用于变更通知
    setBridge(bridge: any): void {
        this.bridge = bridge;
    }

    toJSON() {
        return {
            ...super.toJSON(),
            deviceType: DeviceType.Modem,
            dnsRecords: this.dnsRecords,
            pppoeAuth: this.pppoeAuth,
        };
    }

    fromJSON(config: any): void {
        super.fromJSON(config);

        if (config && Array.isArray(config.dnsRecords)) {
            this.updateDNSRecords(config.dnsRecords);
        }

        if (config && config.pppoeAuth && config.pppoeAuth.username && config.pppoeAuth.password) {
            this.updatePPPOEAuth(config.pppoeAuth.username, config.pppoeAuth.password);
        }
    }

    // 处理PPPOE服务器逻辑
    PPPOE_Recv(fromMac: string, portMac: string, packet: PPPOEPacket, callback: Function): boolean {
        switch (packet.type) {
            case PPPOE_COMMAND_TYPE.PADI:
                {
                    const { username, password } = packet;
                    const { username: serverUser, password: serverPass } = this.pppoeAuth;
                    if (username !== serverUser || password !== serverPass) {
                        callback({ err: '账号密码无效，请双击调制解调器查看！' });
                        return true;
                    }

                    const { ipPoolRangeStart, ipPoolRangeEnd, ipConfig } = this;
                    const { ipAddress: mainAddress, subnetMask, gateway, dns } = ipConfig;

                    // 检查MAC是否已经被分配IP了
                    let ipAddress = this.pppoeMac2IPMap[fromMac];

                    // 如果没有分配IP
                    if (!ipAddress) {
                        // 通过自身IP与子网掩码生成网段
                        const block = new Netmask(`${mainAddress}/${subnetMask}`);
                        block.forEach((ip: string) => {
                            // 有了就跳过
                            if (ipAddress) {
                                return;
                            }

                            // 如果IP不在允许范围内，返回空跳过
                            const parts = ip.match(/\.(\d+)$/);
                            if (!parts) {
                                return;
                            }

                            const ipIndex = parseInt(parts[1]);
                            if ((ipIndex < ipPoolRangeStart) || (ipIndex > ipPoolRangeEnd)) {
                                return;
                            }

                            // 如果IP在范围内，那么是否已经被分配了，已经被分配了，跳过
                            if (this.pppoeIP2MacMap[ip]) {
                                return;
                            }

                            // 那么分配这个IP
                            ipAddress = ip;
                            this.pppoeIP2MacMap[ip] = fromMac;
                            this.pppoeMac2IPMap[fromMac] = ip;
                        })
                    }

                    // IP池用尽
                    if (!ipAddress) {
                        callback({ err: '公网地址池用尽！' });
                        return true;
                    }

                    // 返回PPPOE PADI包
                    const nextIPConfig: IPConfig = {
                        ipMode: IPMode.PPPOE,
                        ipAddress,
                        subnetMask,
                        gateway,
                        dns,
                        pppoe_username: username,
                        pppoe_password: password
                    };

                    callback({ mac: portMac, ipConfig: nextIPConfig });
                }
                break;
            case PPPOE_COMMAND_TYPE.PADT:
                // 检查MAC是否已经被分配IP了
                const ipAddress = this.pppoeMac2IPMap[fromMac];
                if (ipAddress) {
                    delete this.pppoeIP2MacMap[ipAddress];
                    delete this.pppoeMac2IPMap[fromMac]
                };
                break;
            // 由底层设备转发
            case PPPOE_COMMAND_TYPE.PPP:
                return false;
        }

        return true;
    }

    // Modem要模拟DNS服务器和HTTP服务器
    // DNS服务器模拟器
    // HTTP服务器模拟器
    IP_Recv(fromIP: string, toIP: string, protocol: ProtocolType, data: object, TTL: number, callback: Function): any {
        // TTL生存期逻辑，每一跳减一
        TTL--;

        // TTL为零包应当丢弃
        if (TTL <= 0) {
            return null;
        }

        const { ipConfig, dnsRecords } = this;
        const { dns } = ipConfig;

        // 检查是否为需要处理的IP包，否则丢弃
        const routerTableItem = this.getRouterByFromIP(toIP);
        if (!routerTableItem && (toIP !== dns) && (!dnsRecords.find(r => r.ip === toIP))) {
            return null;
        }

        switch (protocol) {
            case ProtocolType.ICMP:
                {
                    const responsePacket = {
                        data,
                        TTL,
                        fromIP: toIP,
                        toIP: fromIP
                    };

                    callback(responsePacket);
                }
                break;
            case ProtocolType.DNS:
                {
                    const packet = data as DNSPacket;
                    const record = dnsRecords.find((r) => r.domainName === packet.domainName);
                    callback(record);
                }
                break;
            case ProtocolType.HTTP:
                {
                    const url = data as URL;
                    const targetServer = url.host;
                    const record = dnsRecords.find((r) => (r.domainName === targetServer) || (r.ip === targetServer));
                    if (!record) {
                        return;
                    }

                    // 判断路径
                    if (url.pathname !== '/') {
                        const response: HTTPResponse = {
                            code: 404,
                            title: `404错误 路径${url.pathname}无法在服务器上找到`
                        };

                        callback(response);
                        return;
                    }

                    // 判断用户名密码
                    if (url.username !== '' || url.password !== '') {
                        const response: HTTPResponse = {
                            code: 403,
                            title: `403错误 对不起，${url.username}没有权限访问这个页面`
                        };

                        callback(response);
                        return;
                    }

                    // 成功响应
                    const response: HTTPResponse = {
                        code: 200,
                        title: `欢迎访问${record.domainName}`,
                    };

                    callback(response);
                }
                break;
            default:
                console.error('未处理报文', fromIP, toIP, protocol, data, TTL);
        }

        return null;
    }

    async destory() {
        // 如果已经建立了PPPOE连接，通知客户端断线
        const ports = this.getPorts();
        // 兜底：无端口或未连接时直接销毁，避免找不到对端
        if (!ports || !ports.length) {
            super.destory();
            return;
        }
        const port = ports[0];
        if (!port || !port.connected) {
            super.destory();
            return;
        }
        try {
            port.HAL_Send(BROARDCAST_MAC, HALFrameType.PPPoE, {
                type: PPPOE_COMMAND_TYPE.PADT,
                mac: port.mac,
            }, () => {
                super.destory();
            });
        } catch (e) {
            // 若发送失败（对端已不存在），直接销毁避免异常
            super.destory();
        }
    }
}
